import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  CrossCircledIcon,
  DotsVerticalIcon,
  Pencil1Icon,
  TrashIcon,
  PersonIcon,
  CheckCircledIcon,
  ClockIcon,
  IdCardIcon,
  EyeOpenIcon,
  EyeClosedIcon,
  Cross2Icon,
  EnvelopeClosedIcon,
  LockClosedIcon,
} from '@radix-ui/react-icons';
import './Global.css';

// Espelha o enum CivilBond do backend (backend/src/entities/user.entity.ts).
const VINCULOS = [
  'Efetivo',
  'Contratado',
  'Terceirizado',
  'Cedido',
  'Voluntário',
];

const EMPTY_FORM = {
  name: '',
  cpf: '',
  matricula: '',
  email: '',
  phone: '',
  civilBond: 'Contratado',
  positionIds: [],
  horasAulas: '',
  contractualHoursPerDay: '',
  password: '',
  isActive: true,
};

const CiviliansManagement = () => {
  const [civilians, setCivilians] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [bondFilter, setBondFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);

  const [civilianToDelete, setCivilianToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- máscaras ----------

  const formatCpf = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) {
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    }
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const formatPhone = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const onlyDigits = (value) => (value || '').replace(/\D/g, '');

  const removeAccents = (str) =>
    (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  // ---------- carga ----------

  const loadCivilians = async () => {
    try {
      const response = await api.get('/civilians');
      setCivilians(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar civis:', error);
      toast.error('Erro ao carregar civis');
    } finally {
      setLoading(false);
    }
  };

  const loadPositions = async () => {
    try {
      // Civil exerce funções próprias (Serviços Gerais, portaria, etc.),
      // cadastradas em Funções com "Destinada a: Pessoal civil".
      const response = await api.get('/positions/active?personnelType=civil');
      setPositions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar funções:', error);
      toast.error('Erro ao carregar funções');
    }
  };

  useEffect(() => {
    loadCivilians();
    loadPositions();
  }, []);

  // ---------- filtros ----------

  const filtered = useMemo(() => {
    const term = removeAccents(searchTerm);
    return civilians
      .filter((civilian) => {
        const funcoes = civilian.positions?.map((p) => p.name).join(' ') || '';
        const matchesSearch =
          !term ||
          removeAccents(civilian.name).includes(term) ||
          removeAccents(civilian.email).includes(term) ||
          removeAccents(funcoes).includes(term) ||
          onlyDigits(civilian.cpf).includes(onlyDigits(searchTerm)) ||
          removeAccents(civilian.matricula).includes(term);

        const matchesBond =
          bondFilter === 'all' || civilian.civilBond === bondFilter;

        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' ? civilian.isActive : !civilian.isActive);

        return matchesSearch && matchesBond && matchesStatus;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [civilians, searchTerm, bondFilter, statusFilter]);

  const pagination = usePagination(filtered, 10);
  const { setPage } = pagination;

  useEffect(() => {
    setPage(1);
  }, [searchTerm, bondFilter, statusFilter, setPage]);

  const stats = useMemo(() => {
    const ativos = civilians.filter((c) => c.isActive);
    const funcoes = new Set();
    ativos.forEach((c) => (c.positions || []).forEach((p) => funcoes.add(p.id)));
    return {
      total: civilians.length,
      ativos: ativos.length,
      inativos: civilians.length - ativos.length,
      funcoes: funcoes.size,
    };
  }, [civilians]);

  const currentItems = pagination.pageItems;

  // ---------- modal ----------

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setShowPassword(false);
    setShowModal(true);
  };

  const openEdit = (civilian) => {
    setEditing(civilian);
    setFormData({
      name: civilian.name || '',
      cpf: formatCpf(civilian.cpf || ''),
      matricula: civilian.matricula || '',
      email: civilian.email || '',
      phone: formatPhone(civilian.phone || ''),
      civilBond: civilian.civilBond || '',
      positionIds: civilian.positions?.map((p) => p.id) || [],
      horasAulas: civilian.horasAulas ?? '',
      contractualHoursPerDay: civilian.contractualHoursPerDay ?? '',
      password: '',
      isActive: civilian.isActive !== false,
    });
    setShowPassword(false);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditing(null);
    setFormData(EMPTY_FORM);
  };

  const togglePosition = (positionId) => {
    setFormData((prev) => {
      const set = new Set(prev.positionIds);
      if (set.has(positionId)) set.delete(positionId);
      else set.add(positionId);
      return { ...prev, positionIds: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cpfDigits = onlyDigits(formData.cpf);
    if (!formData.name.trim()) {
      toast.error('Informe o nome completo');
      return;
    }
    if (cpfDigits.length !== 11) {
      toast.error('CPF deve ter 11 dígitos');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    const payload = {
      name: formData.name.trim().toUpperCase(),
      cpf: cpfDigits,
      matricula: formData.matricula.trim() || undefined,
      email: formData.email.trim() ? formData.email.trim().toLowerCase() : undefined,
      phone: onlyDigits(formData.phone) || undefined,
      civilBond: formData.civilBond || undefined,
      positionIds: formData.positionIds,
      horasAulas: formData.horasAulas ? parseInt(formData.horasAulas, 10) : undefined,
      contractualHoursPerDay: formData.contractualHoursPerDay
        ? parseInt(formData.contractualHoursPerDay, 10)
        : undefined,
      isActive: formData.isActive,
    };

    if (formData.password) {
      payload.password = formData.password;
    }

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) delete payload[key];
    });

    // No update, campo esvaziado precisa virar null explícito — `undefined`
    // some na serialização e o backend manteria o valor antigo.
    if (editing) {
      if (!formData.matricula.trim()) payload.matricula = null;
      if (!onlyDigits(formData.phone)) payload.phone = null;
      if (!formData.civilBond) payload.civilBond = null;
    }

    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/civilians/${editing.id}`, payload);
        toast.success('Civil atualizado com sucesso!');
      } else {
        await api.post('/civilians', payload);
        toast.success('Civil cadastrado com sucesso!');
      }
      closeModal();
      loadCivilians();
    } catch (error) {
      console.error('Erro ao salvar civil:', error);
      const msg = error.response?.data?.message;
      toast.error(
        'Erro ao salvar: ' + (Array.isArray(msg) ? msg.join(', ') : msg || error.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!civilianToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/civilians/${civilianToDelete.id}`);
      toast.success('Civil excluído com sucesso!');
      loadCivilians();
    } catch (error) {
      console.error('Erro ao excluir civil:', error);
      toast.error(
        'Erro ao excluir: ' + (error.response?.data?.message || error.message),
      );
    } finally {
      setDeleting(false);
      setCivilianToDelete(null);
    }
  };

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.error('Nenhum civil para exportar');
      return;
    }

    const rows = filtered.map((civilian) => ({
      CPF: formatCpf(civilian.cpf) || '-',
      'Nome Completo': civilian.name,
      Matrícula: civilian.matricula || '-',
      Vínculo: civilian.civilBond || '-',
      Função: civilian.positions?.map((p) => p.name).join(', ') || '-',
      'Horas Aulas': civilian.horasAulas || '-',
      'E-mail': civilian.email || '-',
      Telefone: formatPhone(civilian.phone) || '-',
      Status: civilian.isActive ? 'Ativo' : 'Inativo',
    }));

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            if (
              typeof value === 'string' &&
              (value.includes(',') || value.includes('"'))
            ) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    link.href = url;
    link.download = `civis_cmcb_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    toast.success(`${filtered.length} civil(is) exportado(s)!`);
  };

  // ---------- render ----------

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando civis...</p>
        </div>
      </>
    );
  }

  const funcoesLabel = (civilian) =>
    civilian.positions?.map((p) => p.name).join(', ') || civilian.position || '—';

  const renderActions = (civilian) => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="icon-button" aria-label={`Ações de ${civilian.name}`}>
          <DotsVerticalIcon />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="dropdown-content" sideOffset={5} align="end">
          <DropdownMenu.Item className="dropdown-item" onSelect={() => openEdit(civilian)}>
            <Pencil1Icon />
            Editar
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="dropdown-separator" />
          <DropdownMenu.Item
            className="dropdown-item dropdown-item-danger"
            onSelect={() => setCivilianToDelete(civilian)}
          >
            <TrashIcon />
            Excluir
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1>Civis</h1>
            <p className="subtitle">
              Pessoal civil do CMCB — serviços gerais, apoio, portaria e também quem
              ministra hora-aula. Cadastro separado do efetivo militar, com o mesmo
              fluxo de ponto.
            </p>
          </div>
          <div className="header-actions">
            <button type="button" className="btn-secondary" onClick={exportCsv}>
              Exportar CSV
            </button>
            <button type="button" className="btn-primary" onClick={openCreate}>
              + Novo Civil
            </button>
          </div>
        </div>

        <div className="civil-stats">
          <div className="civil-stat">
            <span className="civil-stat__icon">
              <PersonIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.total}</strong>
              <span>Civis cadastrados</span>
            </div>
          </div>
          <div className="civil-stat civil-stat--success">
            <span className="civil-stat__icon">
              <CheckCircledIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.ativos}</strong>
              <span>Ativos</span>
            </div>
          </div>
          <div className="civil-stat civil-stat--inactive">
            <span className="civil-stat__icon">
              <CrossCircledIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.inativos}</strong>
              <span>Inativos</span>
            </div>
          </div>
          <div className="civil-stat civil-stat--roles">
            <span className="civil-stat__icon">
              <IdCardIcon width={18} height={18} />
            </span>
            <div>
              <strong>{stats.funcoes}</strong>
              <span>Funções ocupadas</span>
            </div>
          </div>
        </div>

        <div className="table-controls">
          <div className="search-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por nome, CPF, matrícula, e-mail ou função..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="role-filter">
            <label htmlFor="bond-filter">Vínculo:</label>
            <select
              id="bond-filter"
              className="role-select"
              value={bondFilter}
              onChange={(e) => setBondFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              {VINCULOS.map((vinculo) => (
                <option key={vinculo} value={vinculo}>
                  {vinculo}
                </option>
              ))}
            </select>
          </div>
          <div className="role-filter">
            <label htmlFor="status-filter">Status:</label>
            <select
              id="status-filter"
              className="role-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state-container civil-empty">
            <PersonIcon width={40} height={40} />
            <p className="empty-title">
              {civilians.length === 0
                ? 'Nenhum civil cadastrado ainda'
                : 'Nenhum civil encontrado'}
            </p>
            <p className="empty-subtitle">
              {civilians.length === 0
                ? 'Clique em "+ Novo Civil" para cadastrar o primeiro.'
                : 'Ajuste a busca ou os filtros para ver outros resultados.'}
            </p>
          </div>
        ) : (
          <>
            <div className="table-wrapper civil-only-desktop">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Civil</th>
                    <th>CPF</th>
                    <th>Vínculo</th>
                    <th>Funções</th>
                    <th className="text-center">Horas Aulas</th>
                    <th>Contato</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((civilian) => (
                    <tr key={civilian.id}>
                      <td className="cell-primary">
                        <div className="civil-name">
                          <span className="badge badge-civil">Civil</span>
                          <div>
                            <strong>{civilian.name}</strong>
                            {civilian.matricula && (
                              <span className="civil-name__sub">
                                Mat. {civilian.matricula}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="matricula-text civil-cpf">
                          {formatCpf(civilian.cpf) || '—'}
                        </span>
                      </td>
                      <td>{civilian.civilBond || '—'}</td>
                      <td className="civil-roles-cell">{funcoesLabel(civilian)}</td>
                      <td className="text-center">
                        {civilian.horasAulas ? `${civilian.horasAulas}h` : '—'}
                      </td>
                      <td>
                        <div className="civil-contact">
                          <span>{civilian.email || '—'}</span>
                          {civilian.phone && <span>{formatPhone(civilian.phone)}</span>}
                        </div>
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${civilian.isActive ? 'badge-success' : 'badge-danger'}`}
                        >
                          {civilian.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="text-center">{renderActions(civilian)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="civil-cards civil-only-mobile">
              {currentItems.map((civilian) => (
                <article key={civilian.id} className="civil-card">
                  <header className="civil-card__head">
                    <div>
                      <span className="badge badge-civil">Civil</span>
                      <h3>{civilian.name}</h3>
                    </div>
                    {renderActions(civilian)}
                  </header>
                  <dl className="civil-card__grid">
                    <div>
                      <dt>CPF</dt>
                      <dd>{formatCpf(civilian.cpf) || '—'}</dd>
                    </div>
                    <div>
                      <dt>Vínculo</dt>
                      <dd>{civilian.civilBond || '—'}</dd>
                    </div>
                    <div>
                      <dt>Horas-aula</dt>
                      <dd>
                        {civilian.horasAulas ? `${civilian.horasAulas}h` : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>
                        <span
                          className={`badge ${civilian.isActive ? 'badge-success' : 'badge-danger'}`}
                        >
                          {civilian.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </dd>
                    </div>
                    <div className="civil-card__wide">
                      <dt>Funções</dt>
                      <dd>{funcoesLabel(civilian)}</dd>
                    </div>
                    <div className="civil-card__wide">
                      <dt>Contato</dt>
                      <dd>
                        {civilian.email || '—'}
                        {civilian.phone ? ` · ${formatPhone(civilian.phone)}` : ''}
                      </dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>

            <Pagination
              id="civilians"
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              from={pagination.from}
              to={pagination.to}
              pageSize={pagination.pageSize}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
              itemLabel="civis"
            />
          </>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal civ-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="civ-modal-title"
            >
              <header className="civ-modal__hero">
                <span className="civ-modal__avatar">
                  <PersonIcon width={22} height={22} />
                </span>
                <div className="civ-modal__heading">
                  <h2 id="civ-modal-title">
                    {editing ? formData.name || 'Editar civil' : 'Novo civil'}
                  </h2>
                  <p>
                    {editing
                      ? 'Alterações valem imediatamente para o fiscal e os relatórios.'
                      : 'Pessoal civil do CMCB — separado do efetivo militar, com o mesmo fluxo de ponto.'}
                  </p>
                </div>
                <button
                  type="button"
                  className="civ-modal__close"
                  onClick={closeModal}
                  disabled={saving}
                  aria-label="Fechar"
                >
                  <Cross2Icon width={16} height={16} />
                </button>
              </header>

              <form onSubmit={handleSubmit} className="civ-form">
                <div className="civ-modal__body">
                  <section className="civ-section">
                    <h3 className="civ-section__title">
                      <IdCardIcon width={14} height={14} />
                      Identificação
                    </h3>

                    <div className="form-group">
                      <label>Nome completo *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value.toUpperCase() })
                        }
                        placeholder="Ex.: MARIA DE SOUSA LIMA"
                        required
                        autoFocus
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>CPF *</label>
                        <input
                          type="text"
                          value={formData.cpf}
                          onChange={(e) =>
                            setFormData({ ...formData, cpf: formatCpf(e.target.value) })
                          }
                          placeholder="000.000.000-00"
                          inputMode="numeric"
                          maxLength={14}
                          required
                        />
                        <small className="form-hint">
                          Identifica o civil e serve de senha padrão.
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Matrícula / registro</label>
                        <input
                          type="text"
                          value={formData.matricula}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              matricula: e.target.value.toUpperCase(),
                            })
                          }
                          placeholder="Opcional"
                        />
                        <small className="form-hint">
                          Só com matrícula funcional o civil entra na planilha SMAEI.
                        </small>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Vínculo</label>
                      <div className="civ-bonds">
                        {VINCULOS.map((vinculo) => (
                          <button
                            key={vinculo}
                            type="button"
                            className={`civil-chip ${formData.civilBond === vinculo ? 'civil-chip--on' : ''}`}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                civilBond: formData.civilBond === vinculo ? '' : vinculo,
                              })
                            }
                            aria-pressed={formData.civilBond === vinculo}
                          >
                            {vinculo}
                          </button>
                        ))}
                      </div>
                    </div>
                  </section>

                  <section className="civ-section">
                    <h3 className="civ-section__title">
                      <EnvelopeClosedIcon width={14} height={14} />
                      Contato
                    </h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>E-mail</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          placeholder="Em branco, gerado pelo CPF"
                        />
                        <small className="form-hint">
                          Para onde vão os avisos de entrada e saída.
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Telefone</label>
                        <input
                          type="text"
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: formatPhone(e.target.value) })
                          }
                          placeholder="(85) 90000-0000"
                          inputMode="numeric"
                          maxLength={15}
                        />
                      </div>
                    </div>
                  </section>

                  <section className="civ-section">
                    <h3 className="civ-section__title">
                      <ClockIcon width={14} height={14} />
                      Atuação
                    </h3>

                    <div className="form-group">
                      <label>Funções</label>
                      {positions.length === 0 ? (
                        <div className="civ-empty-inline">
                          <strong>Nenhuma função de civil cadastrada.</strong>
                          <span>
                            Civil tem funções próprias — Serviços Gerais, portaria,
                            apoio. Cadastre em <Link to="/admin/positions">Funções</Link>,
                            marcando "Destinada a: Pessoal civil".
                          </span>
                        </div>
                      ) : (
                        <div className="civil-positions">
                          {positions.map((position) => {
                            const checked = formData.positionIds.includes(position.id);
                            return (
                              <label
                                key={position.id}
                                className={`civil-chip ${checked ? 'civil-chip--on' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePosition(position.id)}
                                />
                                {position.name}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Horas-aula semanais</label>
                        <input
                          type="number"
                          min="1"
                          max="40"
                          value={formData.horasAulas}
                          onChange={(e) =>
                            setFormData({ ...formData, horasAulas: e.target.value })
                          }
                          placeholder="Ex.: 20"
                        />
                        <small className="form-hint">
                          Só para quem ministra aula. Quem não ministra bate ponto
                          normalmente — deixe em branco.
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Carga horária diária</label>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          value={formData.contractualHoursPerDay}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              contractualHoursPerDay: e.target.value,
                            })
                          }
                          placeholder="Padrão: 8"
                        />
                      </div>
                    </div>
                  </section>

                  <section className="civ-section">
                    <h3 className="civ-section__title">
                      <LockClosedIcon width={14} height={14} />
                      Acesso
                    </h3>

                    <div className="form-group">
                      <label>Senha</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) =>
                            setFormData({ ...formData, password: e.target.value })
                          }
                          placeholder={editing ? 'Deixe vazio para manter' : 'Padrão: o CPF'}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        >
                          {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                        </button>
                      </div>
                      <small className="form-hint">
                        O civil entra pelo CPF ou pelo e-mail.
                      </small>
                    </div>

                    <label
                      className={`civ-switch ${formData.isActive ? 'civ-switch--on' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData({ ...formData, isActive: e.target.checked })
                        }
                      />
                      <span className="civ-switch__track" aria-hidden="true">
                        <span className="civ-switch__thumb" />
                      </span>
                      <span className="civ-switch__text">
                        <strong>{formData.isActive ? 'Ativo' : 'Inativo'}</strong>
                        <span>
                          {formData.isActive
                            ? 'Aparece para o fiscal e pode ter ponto registrado.'
                            : 'Fica fora da lista do fiscal e dos relatórios.'}
                        </span>
                      </span>
                    </label>
                  </section>
                </div>

                <footer className="civ-modal__footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving
                      ? 'Salvando...'
                      : editing
                        ? 'Salvar alterações'
                        : 'Cadastrar civil'}
                  </button>
                </footer>
              </form>
            </div>
          </div>
        )}

        {civilianToDelete && (
          <div className="modal-overlay" onClick={() => !deleting && setCivilianToDelete(null)}>
            <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Excluir civil</h2>
                <button
                  className="modal-close"
                  onClick={() => setCivilianToDelete(null)}
                  disabled={deleting}
                >
                  ×
                </button>
              </div>
              <div className="confirmation-body">
                <p>
                  Tem certeza que deseja excluir <strong>{civilianToDelete.name}</strong>?
                </p>
                <p className="warning-text">
                  Todos os registros de ponto desse civil também serão removidos. Para apenas
                  suspender o acesso, edite o cadastro e desmarque "Ativo".
                </p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setCivilianToDelete(null)}
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CiviliansManagement;
